import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Box,
  Grid,
  useMediaQuery,
  useTheme,
  Divider,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  FormGroup,
  Collapse,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useSelector, useDispatch } from 'react-redux';
import { auth, firestore } from '../../../../../../libs/firebase-service/src';
import {
  setCart,
  setUser,
  userAddresses,
  setDeliveryStore,
  UdpateNote,
} from '../../../../../../libs/redux-slices/src';
import { fetchData, handleToaster } from '../../Utils/UtilsFunctions';
import halal from '../../../assets/HALAL_1697673855 1.png';
import { environment } from '../../../environments/environment';
import { formatPriceFromCents } from '../../Utils/UtilsFunctions';
import { useNavigate } from 'react-router';
import logo from '../../../assets/fattyBattyLogo.png';
import { preloadImage } from '../../Utils/imageCacheUtils';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';
import { useDeliveryAddress } from '../../hooks/useDeliveryAddress';
import DeliveryDialog from '../CartDrawer/DeliveryDialog';

// Static style objects to prevent unnecessary re-renders
const STATIC_STYLES = {
  modifierBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingRight: 1,
    gap: 1,
  },
  modifierTitle: {
    fontFamily: '"Jost", sans-serif',
    fontWeight: 500,
    color: '#172B4D',
    fontSize: '15px',
    textTransform: 'capitalize',
  },
  modifierPrice: {
    fontFamily: '"Jost", sans-serif',
    fontWeight: 600,
    color: '#2C742F',
    fontSize: '14px',
  },
  formControlLabel: {
    margin: 0,
    padding: '8px 0',
    width: '100%',
    '&:hover': {
      backgroundColor: 'rgba(253, 86, 34, 0.05)',
      borderRadius: '8px',
    },
  },
  quantityBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 1,
    paddingRight: 1,
    paddingBottom: 1,
  },
  quantityLabel: {
    fontFamily: '"Jost", sans-serif',
    fontSize: '14px',
    color: '#666',
  },
  quantityContainer: {
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #E0E0E0',
    borderRadius: '20px',
    backgroundColor: '#F8F9FA',
  },
  quantityButton: {
    width: 28,
    height: 28,
    color: '#666',
  },
  quantityText: {
    fontFamily: '"Jost", sans-serif',
    fontWeight: 600,
    fontSize: '14px',
    minWidth: '24px',
    textAlign: 'center',
    color: '#172B4D',
  },
  variationBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingRight: 1,
    gap: 1,
  },
  variationTitle: {
    fontFamily: '"Jost", sans-serif',
    fontWeight: 500,
    color: '#172B4D',
    fontSize: '16px',
    textTransform: 'capitalize',
  },
  variationPrice: {
    fontFamily: '"Jost", sans-serif',
    fontWeight: 600,
    color: '#2C742F',
    fontSize: '16px',
  },
  radioBase: {
    color: '#E0E0E0',
  },
  checkboxBase: {
    color: '#E0E0E0',
  },
  // Dialog styles
  dialogContent: {
    padding: 0,
    height: '100%',
    display: 'flex',
  },
  gridContainer: {
    height: '100%',
  },
  // Mismatch dialog styles
  mismatchPaper: {
    borderRadius: 3,
    overflow: 'hidden',
  },
  mismatchTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 3,
    py: 2,
    borderBottom: '1px solid #F0F0F0',
  },
  mismatchIconBox: (mainColor) => ({
    width: 36,
    height: 36,
    borderRadius: '50%',
    backgroundColor: `${mainColor}20`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  mismatchIcon: (mainColor) => ({
    color: mainColor,
  }),
  mismatchTitleText: {
    fontFamily: 'Jost, sans-serif',
    fontWeight: 700,
    color: '#172B4D',
  },
  mismatchContent: {
    px: 3,
    py: 2.5,
  },
  mismatchBodyText: {
    fontFamily: 'Jost, sans-serif',
    color: '#4A4A4A',
    lineHeight: 1.6,
    marginTop: 1,
  },
  mismatchWarningBox: {
    mt: 1,
  },
  mismatchWarningText: {
    color: '#8E8E93',
    fontFamily: 'Jost, sans-serif',
  },
  mismatchActions: {
    px: 3,
    py: 1,
    gap: 1,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    color: '#000',
    zIndex: 10,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  },
};

// Create Radio/Checkbox sx objects that depend on mainColor
const createRadioSx = (mainColor) => ({
  ...STATIC_STYLES.radioBase,
  '&.Mui-checked': {
    color: mainColor,
  },
});

const createCheckboxSx = (mainColor) => ({
  ...STATIC_STYLES.checkboxBase,
  '&.Mui-checked': {
    color: mainColor,
  },
});

// Memoized radio button for modifier options to prevent unnecessary re-renders
const ModifierRadioOption = memo(
  ({ option, mainColor, formatPriceFromCents }) => (
    <Box sx={STATIC_STYLES.modifierBox}>
      <Typography sx={STATIC_STYLES.modifierTitle}>{option.title}</Typography>
      {option.price > 0 && (
        <Typography sx={STATIC_STYLES.modifierPrice}>
          +${formatPriceFromCents(option.price)}
        </Typography>
      )}
    </Box>
  )
);

ModifierRadioOption.displayName = 'ModifierRadioOption';

// Memoized checkbox label for modifier options
const ModifierCheckboxLabel = memo(
  ({ option, mainColor, formatPriceFromCents }) => (
    <Box sx={STATIC_STYLES.modifierBox}>
      <Typography sx={STATIC_STYLES.modifierTitle}>{option.title}</Typography>
      {option.price > 0 && (
        <Typography sx={STATIC_STYLES.modifierPrice}>
          +${formatPriceFromCents(option.price)}
        </Typography>
      )}
    </Box>
  )
);

ModifierCheckboxLabel.displayName = 'ModifierCheckboxLabel';

const FattyPattyDialog = ({
  open,
  handleClose,
  item,
  handleOpen,
  modifiers = [],
  storeId,
  editCartItem = null,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // Anonymous auth hook for handling cart additions
  const {
    addToCartWithAnonymousCheck,
    isAnonymous,
    initializeAnonymousSession,
  } = useAnonymousAuth();

  // Unified delivery address hook to respect address chosen in ChangeBranchMenu
  const { currentDeliveryAddressId, currentDeliveryAddressData } =
    useDeliveryAddress();

  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [expandedModifiers, setExpandedModifiers] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  // Refs for scrolling to modifiers with errors
  const modifierRefs = useRef({});
  const initialEditStateRef = useRef(null);

  // Memoize Radio and Checkbox sx objects to prevent recreating on each render
  const radioSx = useMemo(() => createRadioSx(environment.mainColor), []);
  const checkboxSx = useMemo(() => createCheckboxSx(environment.mainColor), []);

  // Memoize dynamic dialog styles that depend on isMobile
  const dialogPaperSx = useMemo(
    () => ({
      borderRadius: isMobile ? 0 : '20px',
      margin: isMobile ? 0 : 2,
      width: isMobile ? '100%' : '95%',
      maxWidth: isMobile ? '100%' : '90%',
      height: isMobile ? '100%' : 'auto',
    }),
    [isMobile]
  );

  const gridItemSx = useMemo(
    () => ({ height: isMobile ? 'auto' : '85vh' }),
    [isMobile]
  );

  const galleryBoxSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row-reverse',
      height: isMobile ? 'auto' : '100%',
      margin: isMobile ? 1 : 2,
      gap: isMobile ? 1 : 2,
    }),
    [isMobile]
  );

  // Memoize button styles that depend on environment.mainColor
  const cancelButtonSx = useMemo(
    () => ({
      textTransform: 'capitalize',
      borderColor: environment.mainColor,
      color: environment.mainColor,
      borderRadius: '10px',
      px: 2.5,
      '&:hover': {
        borderColor: environment.mainColor,
        backgroundColor: `${environment.mainColor}10`,
      },
    }),
    []
  );

  const confirmButtonSx = useMemo(
    () => ({
      textTransform: 'capitalize',
      backgroundColor: environment.mainColor,
      borderRadius: '10px',
      px: 2.5,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: environment.mainColor,
        boxShadow: 'none',
      },
    }),
    []
  );

  const mismatchIconBoxSx = useMemo(
    () => STATIC_STYLES.mismatchIconBox(environment.mainColor),
    []
  );

  const mismatchIconSx = useMemo(
    () => STATIC_STYLES.mismatchIcon(environment.mainColor),
    []
  );

  // Memoize image thumbnail base styles
  const thumbnailBaseSx = useMemo(
    () => ({
      width: isMobile ? '70px' : '100px',
      height: isMobile ? '60px' : '80px',
      borderRadius: '12px',
      overflow: 'hidden',
      cursor: 'pointer',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      transition: 'all 0.3s ease',
      flexShrink: 0,
      '&:hover': {
        border: `3px solid ${environment.mainColor}`,
        transform: 'scale(1.02)',
      },
    }),
    [isMobile]
  );

  // Helper function to get thumbnail sx for each image
  const getThumbnailSx = useCallback(
    (isSelected) => ({
      ...thumbnailBaseSx,
      border: isSelected
        ? `3px solid ${environment.mainColor}`
        : '2px solid #E0E0E0',
    }),
    [thumbnailBaseSx]
  );

  // State for storing API response pricing data
  const [apiPricingData, setApiPricingData] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [hasUserModified, setHasUserModified] = useState(false);
  const isEditingCurrentItem = useMemo(
    () => !!editCartItem && editCartItem?.itemId === item?.id,
    [editCartItem, item?.id]
  );

  // Address dialog state
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressErr, setAddressErr] = useState('');
  const [defaultAdd, setDefaultAdd] = useState('');
  const [addressStore, setAddressStore] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [apiError, setApiError] = useState('');

  // Get addresses from Redux
  const addresses = useSelector((state) => state.authReducer.userAddresses);

  const user = useSelector((state) => state.authReducer.userData);

  // Menu categories (used to derive root menu category for availability windows)
  const categories = useSelector(
    (state) => state.shopReducer.currentCategories
  );

  // Get deliveryStore from Redux to check for stale data
  const deliveryStore = useSelector((state) => state.shopReducer.deliveryStore);
  const deliveryAddress = useSelector(
    (state) => state.shopReducer.deliveryAddress
  );

  // Dialog to prevent mixing items from different stores
  const [storeMismatchOpen, setStoreMismatchOpen] = useState(false);

  // Helper function to check if a discount is valid for the current store
  // Validates discount based on:
  // - present_at_all_locations: true = applies to all stores
  // - present_at_location_ids: array of specific store IDs where discount is valid
  const isDiscountValidForCurrentStore = useCallback(
    (discount) => {
      const effectiveStoreId = storeId || user?.defaultStore;

      // If no store ID is available, don't show discount
      if (!effectiveStoreId) {
        return false;
      }

      // If discount applies to all locations, it's valid
      if (discount.present_at_all_locations === true) {
        return true;
      }

      // If discount doesn't apply to all locations, check if current store is in the allowed list
      if (
        discount.present_at_location_ids &&
        Array.isArray(discount.present_at_location_ids)
      ) {
        return discount.present_at_location_ids.includes(effectiveStoreId);
      }

      // If no location information is available, don't show discount
      return false;
    },
    [storeId, user?.defaultStore]
  );

  // Helper function to check if an option should be displayed at the current location
  const shouldDisplayOption = useCallback((option, currentLocationId) => {
    if (!currentLocationId || !option) {
      return false;
    }

    // Extract location data from option
    const presentAtAllLocations = option.presentAtAllLocations;
    const absentAtLocationIds = option.absentAtLocationIds || [];
    const presentAtLocationIds = option.presentAtLocationIds || [];
    const locationOverrides =
      option.modifierData?.locationOverrides || option.locationOverrides || [];

    // Step 1: Determine initial set of available locations
    let availableLocations = new Set();

    if (presentAtAllLocations === true) {
      // Start with all locations (we'll use a flag since we don't have the complete list)
      // For checking, we assume the location is available unless excluded
      availableLocations.add(currentLocationId); // Assume available initially
    } else if (
      Array.isArray(presentAtLocationIds) &&
      presentAtLocationIds.length > 0
    ) {
      // Start with explicit list of locations
      presentAtLocationIds.forEach((id) => availableLocations.add(id));
    } else {
      // No clear availability rules - default to not available
      return false;
    }

    // Step 2: Remove locations from absentAtLocationIds
    if (Array.isArray(absentAtLocationIds) && absentAtLocationIds.length > 0) {
      absentAtLocationIds.forEach((id) => availableLocations.delete(id));
    }

    // Step 3: Apply locationOverrides - remove locations where soldOut === true
    if (Array.isArray(locationOverrides) && locationOverrides.length > 0) {
      locationOverrides.forEach((override) => {
        if (override.soldOut === true) {
          availableLocations.delete(override.locationId);
        }
        // Future: if soldOut === false, could explicitly add back
        // else if (override.soldOut === false) {
        //   availableLocations.add(override.locationId);
        // }
      });
    }

    // Final check: is current location in the available set?
    const isAvailable = availableLocations.has(currentLocationId);

    return isAvailable;
  }, []);

  // Filter modifiers based on item's modifier_group_ids and store availability
  const itemModifiers = useMemo(() => {
    if (!item?.modifier_group_ids || !modifiers) return [];

    const effectiveStoreId = storeId || user?.defaultStore || null;

    return modifiers
      .filter((modifier) => {
        // First ensure the modifier belongs to the item's modifier groups
        if (!item.modifier_group_ids.includes(modifier.id)) return false;

        // If we don't have a store id, fall back to showing the modifier
        if (!effectiveStoreId) return true;

        // Location rules (Square format):
        // - present_at_all_locations === true => modifier exists at all locations except those listed in absent_at_location_ids
        // - present_at_all_locations === false => present_at_location_ids lists locations where modifier exists
        // If fields are missing, be permissive and show the modifier.

        try {
          const presentAll = modifier.present_at_all_locations;
          const absentIds = Array.isArray(modifier.absent_at_location_ids)
            ? modifier.absent_at_location_ids
            : [];
          const presentIds = Array.isArray(modifier.present_at_location_ids)
            ? modifier.present_at_location_ids
            : [];

          if (presentAll === true) {
            // Present everywhere except those in absentIds
            if (absentIds.length > 0 && absentIds.includes(effectiveStoreId)) {
              return false;
            }
            return true;
          }

          if (presentAll === false) {
            // Only present at explicit locations
            if (presentIds.length > 0) {
              return presentIds.includes(effectiveStoreId);
            }

            // If present_at_all_locations is false but no present_at_location_ids provided,
            // be conservative and do not show the modifier.
            return false;
          }

          // If present_at_all_locations is undefined, default to permissive behavior
          return true;
        } catch (e) {
          // On any unexpected shape, do not block the modifier
          return true;
        }
      })
      .map((modifier) => {
        // Handle different modifier types (LIST, TEXT, or the older format)
        if (modifier.modifier_type === 'LIST') {
          // New Square POS LIST type modifier
          // Use minSelectedModifiers to determine if required, not selection_type
          const isRequired = modifier.minSelectedModifiers > 0;
          const isSingleSelection = modifier.selection_type === 'SINGLE';

          return {
            ...modifier,
            title: modifier.name,
            minPermitted: isRequired ? modifier.minSelectedModifiers || 1 : 0,
            maxPermitted: isSingleSelection
              ? 1
              : modifier.options?.length || 10,
            isRequired: isRequired,
            isSingleSelection: isSingleSelection,
            selectionType: (modifier.selection_type || '').toLowerCase(),
            options: (() => {
              const allOptions = modifier.options || [];
              const filteredOptions = allOptions.filter((option) =>
                shouldDisplayOption(option, effectiveStoreId)
              );
              const mappedOptions = filteredOptions.map((option) => ({
                id: option.id,
                title: option.modifierData?.name || option.name,
                price: option.modifierData?.priceMoney?.amount
                  ? option.modifierData.priceMoney.amount
                  : 0,
              }));

              return mappedOptions;
            })(),
          };
        } else if (modifier.modifier_type === 'TEXT') {
          // New Square POS TEXT type modifier
          return {
            ...modifier,
            title: modifier.name,
            minPermitted: modifier.text_required ? 1 : 0,
            maxPermitted: 1,
            isTextInput: true,
            options: [], // Text modifiers don't have options
          };
        } else if (modifier.title && modifier.options) {
          // Existing modifier format (backward compatibility)
          // Use minSelectedModifiers to determine if required (1 = required, 0 = not required)
          // Use selection_type to determine rendering method (convert to lowercase for consistency)
          const isRequired = modifier.minSelectedModifiers === 1;
          const selectionType = (modifier.selection_type || '').toLowerCase();
          const isSingleSelection = selectionType === 'single';

          // Filter options based on location
          const allOptions = modifier.options || [];
          const filteredOptions = allOptions.filter((option) =>
            shouldDisplayOption(option, effectiveStoreId)
          );

          return {
            ...modifier,
            minPermitted: isRequired ? 1 : 0,
            maxPermitted: isSingleSelection ? 1 : filteredOptions.length || 10,
            isRequired: isRequired,
            isSingleSelection: isSingleSelection,
            selectionType: selectionType, // Store normalized lowercase version
            options: filteredOptions,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [
    item?.modifier_group_ids,
    modifiers,
    storeId,
    user?.defaultStore,
    shouldDisplayOption,
  ]);
  console.log(itemModifiers, 'ss');

  // Prepare images array
  const images = item.listOfImages;

  // Handle image navigation
  const handleImageNavigation = (direction) => {
    if (direction === 'up' && selectedImageIndex > 0) {
      setSelectedImageIndex((prev) => prev - 1);
    } else if (direction === 'down' && selectedImageIndex < images.length - 1) {
      setSelectedImageIndex((prev) => prev + 1);
    }
  };

  // Handle small image click
  const handleSmallImageClick = (index) => {
    setSelectedImageIndex(index);
  };

  // Memoized quantity change handler
  const handleQuantityChange = useCallback((type) => {
    if (type === 'increase') {
      setQuantity((prev) => prev + 1);
      setHasUserModified(true);
    } else if (type === 'decrease') {
      setQuantity((prev) => Math.max(1, prev - 1));
      setHasUserModified(true);
    }
  }, []);

  // Memoized modifier change handler
  const handleModifierChange = useCallback(
    (modifierId, optionId, isChecked = null, textValue = null) => {
      setHasUserModified(true);
      setSelectedModifiers((prev) => {
        const newModifiers = { ...prev };

        if (!newModifiers[modifierId]) {
          newModifiers[modifierId] = [];
        }

        const modifier = itemModifiers?.find((m) => m.id === modifierId);
        if (!modifier) return prev;

        // Handle text input modifiers
        if (modifier.isTextInput) {
          newModifiers[modifierId] = textValue
            ? [
                {
                  id: modifierId,
                  text: textValue,
                  quantity: 1,
                },
              ]
            : [];
        } else if (modifier.isSingleSelection) {
          // Radio button behavior — only when selection_type === 'SINGLE'
          newModifiers[modifierId] = [
            {
              id: optionId,
              quantity: 1,
            },
          ];
        } else {
          // Checkbox behavior (multiple selection)
          if (isChecked) {
            // Check if option already exists
            const existingIndex = newModifiers[modifierId].findIndex((item) =>
              typeof item === 'object'
                ? item.id === optionId
                : item === optionId
            );

            if (existingIndex === -1) {
              newModifiers[modifierId] = [
                ...newModifiers[modifierId],
                {
                  id: optionId,
                  quantity: 1,
                },
              ];
            }
          } else {
            newModifiers[modifierId] = newModifiers[modifierId].filter((item) =>
              typeof item === 'object'
                ? item.id !== optionId
                : item !== optionId
            );
          }
        }

        return newModifiers;
      });

      // Clear validation error separately to avoid unnecessary re-renders
      const modifier = itemModifiers?.find((m) => m.id === modifierId);
      if (modifier?.minPermitted > 0 || modifier?.isRequired) {
        setValidationErrors((prevErrors) => {
          const newErrors = { ...prevErrors };
          delete newErrors[modifierId];
          return newErrors;
        });
      }
    },
    [itemModifiers]
  );

  // Memoized toggle modifier expanded
  const toggleModifierExpanded = useCallback((modifierId) => {
    setExpandedModifiers((prev) => ({
      ...prev,
      [modifierId]: !prev[modifierId],
    }));
  }, []);

  // Memoized modifier quantity change handler
  const handleModifierQuantityChange = useCallback(
    (modifierId, optionId, type) => {
      setHasUserModified(true);
      setSelectedModifiers((prev) => {
        const newModifiers = { ...prev };
        if (!newModifiers[modifierId]) return prev;

        // Copy the inner array so we don't mutate the frozen Redux state reference
        newModifiers[modifierId] = [...newModifiers[modifierId]];

        const optionIndex = newModifiers[modifierId].findIndex((item) =>
          typeof item === 'object' ? item.id === optionId : item === optionId
        );

        if (optionIndex !== -1) {
          const currentItem = newModifiers[modifierId][optionIndex];
          if (typeof currentItem === 'object') {
            const newQuantity =
              type === 'increase'
                ? (currentItem.quantity || 1) + 1
                : Math.max(1, (currentItem.quantity || 1) - 1);

            newModifiers[modifierId][optionIndex] = {
              ...currentItem,
              quantity: newQuantity,
            };
          }
        }

        return newModifiers;
      });
    },
    []
  );

  // Memoized modifier price calculation
  const modifierPrice = useMemo(() => {
    let price = 0;
    if (itemModifiers?.length > 0) {
      itemModifiers.forEach((modifier) => {
        const selectedOptions = selectedModifiers[modifier.id] || [];
        selectedOptions.forEach((selectedOption) => {
          if (typeof selectedOption === 'object') {
            const option = modifier.options?.find(
              (opt) => opt.id === selectedOption.id
            );
            if (option) {
              price +=
                ((option.price || 0) / 100) * (selectedOption.quantity || 1);
            }
          } else {
            const option = modifier.options?.find(
              (opt) => opt.id === selectedOption
            );
            if (option) {
              price += (option.price || 0) / 100;
            }
          }
        });
      });
    }
    return price;
  }, [itemModifiers, selectedModifiers]);

  // Memoized base price
  const basePrice = useMemo(() => {
    const price = selectedVariation ? selectedVariation.price : item.price || 0;
    return price / 100; // Convert cents to dollars for calculations
  }, [selectedVariation, item.price]);

  // Memoized total price calculation
  const totalPrice = useMemo(() => {
    const totalBeforeQuantity = basePrice + modifierPrice;
    return (totalBeforeQuantity * quantity).toFixed(2);
  }, [basePrice, modifierPrice, quantity]);

  // Remove getModifierPrice as we now use the memoized modifierPrice

  // Memoized favorite toggle handler
  const handleFavoriteToggle = useCallback(async () => {
    try {
      if (auth?.currentUser?.uid) {
        const favorites = user?.favorites || [];
        let updatedFavorites;

        if (isFavorite) {
          updatedFavorites = favorites.filter((fav) => fav !== item.id);
        } else {
          updatedFavorites = [...favorites, item.id];
        }

        await firestore
          .collection('users')
          .doc(auth?.currentUser?.uid)
          .set({ favorites: updatedFavorites }, { merge: true });

        const updatedUser = { ...user, favorites: updatedFavorites };
        dispatch(setUser({ userData: updatedUser }));
        setIsFavorite(!isFavorite);
      }
    } catch (error) {
      // Error handled silently
    }
  }, [auth?.currentUser?.uid, user, isFavorite, item.id, dispatch]);

  // Memoized validation function
  const validateRequiredModifiers = useCallback(() => {
    const errors = {};
    let hasErrors = false;

    // Validate variation selection if item has variations
    if (item?.variations && item.variations.length > 0 && !selectedVariation) {
      errors['variation'] = 'Please select a size/variation';
      hasErrors = true;
    }

    // Validate modifiers
    if (itemModifiers && itemModifiers.length > 0) {
      for (const modifier of itemModifiers) {
        // Check if modifier is required using either minPermitted > 0 or isRequired property
        const isModifierRequired =
          modifier.minPermitted > 0 || modifier.isRequired;

        if (isModifierRequired) {
          const selectedOptions = selectedModifiers[modifier.id] || [];
          const requiredCount =
            modifier.minPermitted > 0 ? modifier.minPermitted : 1;

          if (selectedOptions.length < requiredCount) {
            errors[modifier.id] = `Please select ${requiredCount} option${
              requiredCount > 1 ? 's' : ''
            } for ${modifier.title}`;
            hasErrors = true;
          }
        }
      }
    }

    setValidationErrors(errors);
    return { isValid: !hasErrors, errors };
  }, [item?.variations, selectedVariation, itemModifiers, selectedModifiers]);

  // Memoized modifier details
  const modifierDetails = useMemo(() => {
    const details = [];

    if (itemModifiers?.length > 0) {
      itemModifiers.forEach((modifier) => {
        const selectedOptions = selectedModifiers[modifier.id] || [];

        selectedOptions.forEach((selectedOption) => {
          if (modifier.isTextInput) {
            // Handle text input modifiers
            const textValue =
              typeof selectedOption === 'object'
                ? selectedOption.text || ''
                : selectedOption;
            details.push({
              groupTitle: modifier.title,
              optionTitle: textValue,
              price: 0,
              quantity:
                typeof selectedOption === 'object'
                  ? selectedOption.quantity || 1
                  : 1,
              isTextInput: true,
            });
          } else {
            // Handle regular option modifiers
            if (typeof selectedOption === 'object') {
              // New structure with quantity - look up title and price from modifier options
              const option = modifier.options?.find(
                (opt) => opt.id === selectedOption.id
              );
              if (option) {
                details.push({
                  groupTitle: modifier.title,
                  optionTitle: option.title,
                  price: (option.price || 0) / 100,
                  quantity: selectedOption.quantity || 1,
                  isTextInput: false,
                });
              }
            } else {
              // Fallback for old structure (option ID only)
              const option = modifier.options?.find(
                (opt) => opt.id === selectedOption
              );
              if (option) {
                details.push({
                  groupTitle: modifier.title,
                  optionTitle: option.title,
                  price: (option.price || 0) / 100,
                  quantity: 1,
                  isTextInput: false,
                });
              }
            }
          }
        });
      });
    }

    return details;
  }, [itemModifiers, selectedModifiers]);

  // Debounced API price calculation to reduce unnecessary calls
  // calculatePriceFromAPI now accepts an options object: { force: boolean, variationIdOverride: string|null }
  const calculatePriceFromAPI = useCallback(
    async (options = {}) => {
      const { force = false, variationIdOverride = null } = options || {};
      const effectiveStoreId = storeId || user?.defaultStore;
      // Only proceed if we have an authenticated user and a store. Allow forcing the call when needed.
      if (
        !auth?.currentUser?.uid ||
        !effectiveStoreId ||
        (!hasUserModified && !force)
      ) {
        return;
      }

      try {
        setIsCalculatingPrice(true);
        const token = await auth?.currentUser?.getIdToken();
        const headers = {
          'Content-Type': 'application/json',
          Authorization: token,
        };

        // Prepare the item data for API calculation. Allow overriding the variation id when calling immediately on open.
        const itemData = {
          itemId: item.id,
          variationId:
            variationIdOverride !== null
              ? variationIdOverride
              : selectedVariation
              ? selectedVariation.id
              : null,
          quantity: quantity,
          customization: selectedModifiers,
        };

        const requestBody = {
          storeId: effectiveStoreId,
          deliveryOrPickup: 'pickup',
          restaurantId: environment.restaurantId,
          tipAmount: 0,
          uid: auth?.currentUser?.uid,
          items: [itemData], // Add the current item with its modifications
        };

        const response = await fetchData(
          `${environment?.apiUrl}/calculateSquareItemsPrice?restaurantId=${environment.restaurantId}`,
          'POST',
          requestBody,
          headers
        );

        if (response?.data?.priceFromSquare) {
          setApiPricingData(response.data.priceFromSquare);
        }
      } catch (error) {
        // Error handled silently
      } finally {
        setIsCalculatingPrice(false);
      }
    },
    [
      auth?.currentUser?.uid,
      storeId,
      user?.defaultStore,
      hasUserModified,
      item,
      selectedVariation,
      quantity,
      selectedModifiers,
    ]
  );

  // Optimized and memoized add to cart handler with anonymous auth support
  const handleAddToCart = useCallback(async () => {
    // Prevent concurrent add to cart operations
    if (loading) {
      return;
    }

    // Set loading immediately to prevent race conditions
    setLoading(true);

    // Get fresh cart data from Firebase EARLY to avoid race conditions
    let freshCartItems = [];
    try {
      if (auth?.currentUser?.uid) {
        const userDoc = await firestore
          .collection('users')
          .doc(auth.currentUser.uid)
          .get();

        if (userDoc.exists) {
          freshCartItems = userDoc.data()?.bagItems || [];
        }
      } else {
        // Fallback to Redux state for non-authenticated users
        freshCartItems = user?.bagItems || [];
      }
    } catch (error) {
      // Fallback to Redux state on error
      freshCartItems = user?.bagItems || [];
    }

    try {
      const validation = validateRequiredModifiers();
      if (!validation.isValid) {
        // Scroll to first error or auto-expand modifiers with errors
        const firstErrorModifierId = Object.keys(validation.errors)[0];
        if (firstErrorModifierId) {
          setExpandedModifiers((prev) => ({
            ...prev,
            [firstErrorModifierId]: true,
          }));

          // Scroll to the modifier with error after a short delay to allow expansion
          setTimeout(() => {
            const modifierElement = modifierRefs.current[firstErrorModifierId];
            if (modifierElement) {
              modifierElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              });
            }
          }, 100);
        }
        setLoading(false);
        return;
      }

      // Check if order type is delivery - require address before adding to cart
      if (user?.pickupOrDelivery === 'delivery') {
        // Check if we have actual address data, not just an ID
        // For anonymous users or when address data is missing, we need to verify the address actually exists
        const hasValidCurrentAddress =
          currentDeliveryAddressId &&
          typeof currentDeliveryAddressId === 'string' &&
          currentDeliveryAddressId.trim() !== '' &&
          (currentDeliveryAddressData !== null || // Address data is loaded
            (Array.isArray(addresses) &&
              addresses.some((addr) => addr.id === currentDeliveryAddressId))); // Or address exists in list

        const hasValidDefaultAddress =
          user?.defaultDeliveryAddress &&
          typeof user.defaultDeliveryAddress === 'string' &&
          user.defaultDeliveryAddress.trim() !== '' &&
          Array.isArray(addresses) &&
          addresses.some((addr) => addr.id === user.defaultDeliveryAddress); // Address exists in list

        const hasDeliveryAddress =
          hasValidCurrentAddress || hasValidDefaultAddress;

        if (!hasDeliveryAddress) {
          handleToaster('error', 'Please add a delivery address');
          // Open address dialog instead of navigating to profile
          setAddressDialogOpen(true);
          setLoading(false);
          return;
        }
      }

      // Prevent adding items from a different store than items already in the cart
      // Use fresh cart data instead of potentially stale Redux state
      const bagitems = freshCartItems;
      const effectiveStoreId = storeId || user?.defaultStore;

      // Use effectiveStoreId as the primary source since it represents the selected store
      // Only fall back to item.store_id if effectiveStoreId is not available
      const itemStoreId = effectiveStoreId || item?.store_id || null;
      if (bagitems.length > 0) {
        const existingStoreId = bagitems[0]?.storeId;

        // Fixed validation: Only check if both store IDs exist and are different
        if (existingStoreId && itemStoreId && existingStoreId !== itemStoreId) {
          setStoreMismatchOpen(true);
          setLoading(false);
          return;
        }
      } else {
      }

      // Use existing API pricing data if available, otherwise make API call
      let apiResponse = apiPricingData;

      if (!apiResponse) {
        try {
          const token = await auth?.currentUser?.getIdToken();
          const headers = {
            'Content-Type': 'application/json',
            Authorization: token,
          };

          const requestBody = {
            storeId: effectiveStoreId || '',
            deliveryOrPickup: 'pickup',
            restaurantId: environment.restaurantId,
            tipAmount: 0,
            uid: auth?.currentUser?.uid,
          };

          const response = await fetchData(
            `${environment?.apiUrl}/calculateSquareItemsPrice?restaurantId=${environment.restaurantId}`,
            'POST',
            requestBody,
            headers
          );

          apiResponse = response?.data?.priceFromSquare || null;
        } catch (apiError) {
          // Error handled silently
        }
      }

      // Use the fresh cart items we already fetched early to avoid duplicate Firebase calls
      const currentBagItems = freshCartItems;

      // Use API response data for pricing if available, otherwise use memoized calculations
      let itemPrice, calculatedPrice;

      if (
        apiResponse &&
        hasUserModified &&
        apiResponse.totalMoney?.amount !== undefined
      ) {
        // Use backend-calculated pricing with modifiers (convert from cents to dollars)
        const totalAmt = Number(apiResponse.totalMoney.amount) || 0;
        const serviceAmt = Number(apiResponse.serviceChargeMoney?.amount) || 0;
        itemPrice = (totalAmt - serviceAmt) / 100;
        calculatedPrice = itemPrice;
      } else if (apiResponse && apiResponse.totalMoney?.amount !== undefined) {
        // Use backend-calculated pricing without modifiers (convert from cents to dollars)
        const totalAmt = Number(apiResponse.totalMoney.amount) || 0;
        const serviceAmt = Number(apiResponse.serviceChargeMoney?.amount) || 0;
        itemPrice = (totalAmt - serviceAmt) / 100;
        calculatedPrice = itemPrice;
      } else {
        // Use memoized frontend calculation
        itemPrice = basePrice + modifierPrice;
        calculatedPrice = parseFloat(totalPrice);
      }

      // Sanitize function moved outside for better performance
      const sanitizeObject = (obj) => {
        if (obj === null || obj === undefined) return obj;
        if (typeof obj === 'function') return null;
        if (Array.isArray(obj)) {
          return obj
            .map((item) => sanitizeObject(item))
            .filter((item) => item !== null);
        }
        if (typeof obj === 'object') {
          const cleaned = {};
          for (const [key, value] of Object.entries(obj)) {
            const cleanedValue = sanitizeObject(value);
            if (cleanedValue !== null) {
              cleaned[key] = cleanedValue;
            }
          }
          return cleaned;
        }
        return obj;
      };

      const cleanSelectedModifiers = sanitizeObject(selectedModifiers);
      const cleanModifierDetails = sanitizeObject(modifierDetails);

      // Derive the root menu category that controls availability windows.
      // We prefer the parent/rootCategory of the menu category this item belongs to,
      // falling back to that category's own id when there is no parent.
      let availabilityRootCategoryId = null;
      try {
        if (categories && categories.length && item) {
          const itemCategoryIds = Array.isArray(item.category_ids)
            ? item.category_ids
            : item.category_id
            ? [item.category_id]
            : [];

          if (itemCategoryIds.length) {
            const matchedCategory =
              categories.find((c) => itemCategoryIds.includes(c.id)) || null;
            if (matchedCategory) {
              availabilityRootCategoryId =
                matchedCategory.rootCategory || matchedCategory.id || null;
            }
          }
        }
      } catch (e) {
        // In case of any unexpected shape, silently ignore and leave availabilityRootCategoryId as null.
      }

      const newOption = {
        price: itemPrice,
        calculatedPrice: calculatedPrice,
        image: item.item_image_url,
        categoryId:
          item?.category_id ||
          item?.categoryId ||
          (item?.category && item.category.id) ||
          null,
        // Root/menu category used for availability periods (time windows)
        availabilityRootCategoryId,
        // Preserve store information so cart items carry storeId for cross-component consistency
        storeId: effectiveStoreId || item.store_id || null,
        itemId: item.id,
        variationId: selectedVariation ? selectedVariation.id : null,
        specialInstructions: '',
        title: item.name,
        quantity: quantity,
        customization: cleanSelectedModifiers,
        modifiers: cleanSelectedModifiers,
        modifierDetails: cleanModifierDetails,
        itemDiscounts: (item.discounts || []).filter((discount) =>
          isDiscountValidForCurrentStore(discount)
        ),
        apiPricingData: apiResponse
          ? {
              serviceChargeMoney: apiResponse.serviceChargeMoney || {
                amount: '0',
                currency: 'USD',
              },
              discountMoney: apiResponse.discountMoney || {
                amount: '0',
                currency: 'USD',
              },
              taxMoney: apiResponse.taxMoney || {
                amount: '0',
                currency: 'USD',
              },
              tipMoney: apiResponse.tipMoney || {
                amount: '0',
                currency: 'USD',
              },
              totalMoney: apiResponse.totalMoney || {
                amount: '0',
                currency: 'USD',
              },
              lineItemsPrice: apiResponse.lineItemsPrice || [],
            }
          : null,
      };

      // Helper function to check if two customization objects are identical
      const areCustomizationsEqual = (custom1, custom2) => {
        // Handle null/undefined cases
        if (!custom1 && !custom2) return true;
        if (!custom1 || !custom2) return false;

        // Get sorted keys
        const keys1 = Object.keys(custom1).sort();
        const keys2 = Object.keys(custom2).sort();

        // Check if number of keys is different
        if (keys1.length !== keys2.length) return false;

        // Check if keys are the same
        if (JSON.stringify(keys1) !== JSON.stringify(keys2)) return false;

        // Check each modifier group
        for (const key of keys1) {
          const value1 = custom1[key];
          const value2 = custom2[key];

          // Handle arrays (selected modifier options)
          if (Array.isArray(value1) && Array.isArray(value2)) {
            if (value1.length !== value2.length) return false;

            // Sort arrays by id and quantity for consistent comparison
            const sorted1 = [...value1].sort((a, b) => {
              const aId = a?.id || '';
              const bId = b?.id || '';
              const aQty = a?.quantity || 0;
              const bQty = b?.quantity || 0;

              // First sort by id, then by quantity
              if (aId !== bId) return aId.localeCompare(bId);
              return aQty - bQty;
            });

            const sorted2 = [...value2].sort((a, b) => {
              const aId = a?.id || '';
              const bId = b?.id || '';
              const aQty = a?.quantity || 0;
              const bQty = b?.quantity || 0;

              // First sort by id, then by quantity
              if (aId !== bId) return aId.localeCompare(bId);
              return aQty - bQty;
            });

            // Compare each element in the arrays
            for (let i = 0; i < sorted1.length; i++) {
              const obj1 = sorted1[i];
              const obj2 = sorted2[i];

              // Compare id and quantity specifically for modifier options
              if (obj1?.id !== obj2?.id || obj1?.quantity !== obj2?.quantity) {
                return false;
              }
            }
          } else {
            // Direct comparison for non-array values (text inputs, etc.)
            if (JSON.stringify(value1) !== JSON.stringify(value2)) return false;
          }
        }

        return true;
      };

      let items;
      const editCartIndex =
        Number.isInteger(editCartItem?.cartIndex) &&
        editCartItem.cartIndex >= 0 &&
        editCartItem.cartIndex < currentBagItems.length
          ? editCartItem.cartIndex
          : -1;

      if (isEditingCurrentItem && editCartIndex !== -1) {
        items = [...currentBagItems];
        const existingItem = items[editCartIndex] || {};
        items[editCartIndex] = {
          ...existingItem,
          ...newOption,
          quantity,
          price: itemPrice,
          calculatedPrice: quantity * itemPrice,
        };
      } else {
        // Check if an identical item already exists in cart
        const existingItemIndex = currentBagItems.findIndex((cartItem) => {
          const itemIdMatch = cartItem.itemId === item.id;
          const variationMatch =
            cartItem.variationId ===
            (selectedVariation ? selectedVariation.id : null);
          const customizationMatch = areCustomizationsEqual(
            cartItem.customization,
            cleanSelectedModifiers
          );

          return itemIdMatch && variationMatch && customizationMatch;
        });

        if (existingItemIndex !== -1) {
          items = [...currentBagItems];
          const existingItem = items[existingItemIndex];

          // Update the existing item with merged quantity and latest pricing
          items[existingItemIndex] = {
            ...existingItem,
            quantity: existingItem.quantity + quantity,
            // Update with latest pricing data and calculated price
            price: itemPrice,
            calculatedPrice: (existingItem.quantity + quantity) * itemPrice, // Total price for all quantities
            // Update API pricing data if available
            ...(apiResponse && {
              apiPricingData: {
                ...newOption.apiPricingData,
                // Update total money to reflect new quantity
                totalMoney: {
                  ...newOption.apiPricingData.totalMoney,
                  amount: String(
                    Math.round(
                      (existingItem.quantity + quantity) * itemPrice * 100
                    )
                  ),
                },
              },
            }),
          };
        } else {
          // Item with different customizations or new item, add as new entry
          items = [...currentBagItems, newOption];
        }
      }

      // Add item to cart with anonymous support
      // First check if we need to initialize anonymous session
      if (!auth?.currentUser?.uid) {
        // Pass user's delivery preference, defaulting to 'pickup' if not available
        const deliveryPreference = user?.pickupOrDelivery || 'pickup';
        const initResult = await addToCartWithAnonymousCheck(
          newOption,
          deliveryPreference
        );
        if (!initResult.success) {
          throw new Error(
            initResult.error || 'Failed to initialize anonymous session'
          );
        }

        // After anonymous session is initialized, we need to add the item to Firebase
        // Wait a moment for auth state to update
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Get the current user ID after anonymous sign-in
        const currentUserId = auth?.currentUser?.uid;
        if (currentUserId) {
          // Add to Firebase
          await firestore
            .collection('users')
            .doc(currentUserId)
            .update({
              bagItems: items,
              lastActiveAt: new Date(),
              'anonymousSession.hasAddedItems': true,
              'anonymousSession.cartStartedAt':
                items.length === 1
                  ? new Date()
                  : firestore.FieldValue.serverTimestamp(),
            });

          // Update Redux
          dispatch(setCart(items));
          const updatedUser = { ...user, bagItems: items, uid: currentUserId };
          dispatch(setUser({ userData: updatedUser }));
        } else {
          throw new Error(
            'Failed to get user ID after anonymous initialization'
          );
        }
      } else {
        // Regular authenticated user flow

        await firestore
          .collection('users')
          .doc(auth?.currentUser?.uid)
          .set({ bagItems: items }, { merge: true });

        dispatch(setCart(items));
        const updatedUser = { ...user, bagItems: items };
        dispatch(setUser({ userData: updatedUser }));
      }

      setAdded(true);
      setTimeout(() => {
        setAdded(false);
        handleClose();
      }, 1500);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  }, [
    validateRequiredModifiers,
    validationErrors,
    auth?.currentUser?.uid,
    apiPricingData,
    user,
    basePrice,
    modifierPrice,
    totalPrice,
    selectedModifiers,
    modifierDetails,
    item,
    selectedVariation,
    quantity,
    editCartItem,
    isEditingCurrentItem,
    loading,
    dispatch,
    handleClose,
    addToCartWithAnonymousCheck,
    isDiscountValidForCurrentStore,
    currentDeliveryAddressId,
    currentDeliveryAddressData,
    addresses,
  ]);

  const handleClearCartAndProceed = useCallback(async () => {
    try {
      if (!auth?.currentUser?.uid) {
        setStoreMismatchOpen(false);
        return;
      }
      await firestore
        .collection('users')
        .doc(auth?.currentUser?.uid)
        .set({ bagItems: [] }, { merge: true });

      dispatch(setCart([]));
      dispatch(UdpateNote(''));
      const updatedUser = { ...user, bagItems: [] };
      dispatch(setUser({ userData: updatedUser }));
      setStoreMismatchOpen(false);
    } catch (e) {
      // Error handled silently
      setStoreMismatchOpen(false);
    }
  }, [auth?.currentUser?.uid, dispatch, user]);

  // Handle address change from DeliveryDialog
  const handleChangeAddress = useCallback(
    async (addressId) => {
      try {
        // Ensure user session exists before trying to access user document
        if (!user?.uid) {
          // Since user is trying to change address, they clearly want delivery
          const sessionResult = await initializeAnonymousSession('delivery');
          if (!sessionResult.success) {
            setApiError('Failed to initialize user session');
            return;
          }
          // Wait a moment for Redux to update with new user data
          await new Promise((resolve) => setTimeout(resolve, 500));
        }

        const currentUserId = user?.uid || auth?.currentUser?.uid;
        if (!currentUserId) {
          setApiError('User session not available');
          return;
        }

        const addressesRef = firestore
          .collection('users')
          .doc(currentUserId)
          .collection('addresses');

        const addressesSnapshot = await addressesRef.get();
        const addressesList = [];

        addressesSnapshot.forEach((doc) => {
          addressesList.push({ id: doc.id, ...doc.data() });
        });

        const selectedAddress = addressesList.find((a) => a.id === addressId);

        if (selectedAddress) {
          // Update user document with new defaultDeliveryAddress
          await firestore
            .collection('users')
            .doc(currentUserId)
            .update({ defaultDeliveryAddress: addressId });

          // Update Redux user data
          const updatedUser = { ...user, defaultDeliveryAddress: addressId };
          dispatch(setUser({ userData: updatedUser }));

          // Update Redux addresses if needed
          dispatch(userAddresses(addressesList));

          setDefaultAdd(
            selectedAddress?.displayName || selectedAddress?.address
          );
          setApiError('');
          setDeliveryFee(0);

          // Close address dialog
          setAddressDialogOpen(false);

          // Show success message
          handleToaster('success', 'Delivery address updated successfully');
        }
      } catch (error) {
        setApiError('Failed to change address');
      }
    },
    [user?.uid, dispatch, user, initializeAnonymousSession]
  );

  // Reset states when dialog opens
  useEffect(() => {
    if (open && item) {
      const editCustomization =
        editCartItem?.customization || editCartItem?.modifiers || {};
      const hasEditCustomization =
        editCustomization &&
        typeof editCustomization === 'object' &&
        Object.keys(editCustomization).length > 0;

      const initialQty =
        isEditingCurrentItem && Number(editCartItem?.quantity) > 0
          ? Number(editCartItem.quantity)
          : 1;
      setQuantity(initialQty);
      setAdded(false);
      setSelectedImageIndex(0);
      setSelectedModifiers(isEditingCurrentItem ? editCustomization : {});
      setValidationErrors({});
      setHasUserModified(isEditingCurrentItem && hasEditCustomization);
      setApiPricingData(null);

      if (isEditingCurrentItem) {
        initialEditStateRef.current = {
          quantity: initialQty,
          modifiers: JSON.stringify(editCustomization),
          variationId: editCartItem?.variationId || null,
        };
      } else {
        initialEditStateRef.current = null;
      }

      // Preload all dialog images for better performance
      if (item.listOfImages && item.listOfImages.length > 0) {
        item.listOfImages.forEach((imageSrc) => {
          preloadImage(imageSrc).catch(() => {
            // Error handled silently
          });
        });
      }
      // Also preload the main item image if it exists
      if (item.item_image_url) {
        preloadImage(item.item_image_url).catch(() => {
          // Error handled silently
        });
      }

      // Set default variation to the lowest-priced available variation for this store
      const effectiveStoreId = storeId || user?.defaultStore || null;

      const getVariationPrice = (v) => {
        try {
          // New Square structure
          if (v?.itemVariationData?.priceMoney?.amount) {
            return Number(v.itemVariationData.priceMoney.amount);
          }
          // Older / normalized structure
          if (v?.price !== undefined && v.price !== null) {
            return Number(v.price);
          }
          if (v?.priceMoney?.amount) {
            return Number(v.priceMoney.amount);
          }
        } catch (e) {
          return Infinity;
        }
        return Infinity;
      };

      const isVariationAvailableForStore = (v) => {
        try {
          // Use Square-style flags when present
          const presentAll = v?.presentAtAllLocations;
          const absentIds = Array.isArray(v?.absentAtLocationIds)
            ? v.absentAtLocationIds
            : Array.isArray(v?.absent_at_location_ids)
            ? v.absent_at_location_ids
            : [];
          const presentIds = Array.isArray(v?.presentAtLocationIds)
            ? v.presentAtLocationIds
            : Array.isArray(v?.present_at_location_ids)
            ? v.present_at_location_ids
            : [];

          if (!effectiveStoreId) return true;

          if (presentAll === true) {
            if (absentIds.length > 0 && absentIds.includes(effectiveStoreId)) {
              return false;
            }
            return true;
          }

          if (presentAll === false) {
            if (presentIds.length > 0) {
              return presentIds.includes(effectiveStoreId);
            }
            return false;
          }

          return true;
        } catch (e) {
          return true;
        }
      };

      let defaultVariation = null;
      if (item.variations && item.variations.length > 0) {
        const available = item.variations.filter(isVariationAvailableForStore);
        if (available.length === 0) {
          // fallback to all variations
          defaultVariation = item.variations[0];
        } else {
          // choose the variation with the lowest price (prefer priceAfterDiscount if available)
          defaultVariation = available.reduce((best, cur) => {
            const bestPrice = getVariationPrice(best);
            const curPrice = getVariationPrice(cur);
            return curPrice < bestPrice ? cur : best;
          }, available[0]);
        }
      }

      const selectedVariationFromEdit =
        isEditingCurrentItem && editCartItem?.variationId
          ? item?.variations?.find((v) => v?.id === editCartItem.variationId) ||
            null
          : null;

      const initialVariation = selectedVariationFromEdit || defaultVariation;
      setSelectedVariation(initialVariation || null);

      // Auto-expand ALL modifier groups when dialog opens
      const initialExpandedState = {};
      if (itemModifiers && itemModifiers.length > 0) {
        // ensure every modifier group is open
        itemModifiers.forEach((modifier) => {
          initialExpandedState[modifier.id] = true;
        });
      }
      setExpandedModifiers(initialExpandedState);

      // Immediately calculate price for the default (first) variation when dialog opens
      // Pass force=true to bypass hasUserModified gating and pass the variation id explicitly
      try {
        const defaultVariationId = initialVariation ? initialVariation.id : null;
        // Fire-and-forget: calculate price for default selection
        calculatePriceFromAPI({
          force: true,
          variationIdOverride: defaultVariationId,
        });
      } catch (e) {
        // Non-fatal: error handled silently
      }
    }
  }, [open, item, modifiers, isEditingCurrentItem, editCartItem]);

  // Separate effect to sync favorite state when dialog opens (without triggering price calculation)
  useEffect(() => {
    if (open && item?.id) {
      const favorites = user?.favorites || [];
      setIsFavorite(favorites.includes(item.id));
    }
  }, [open, item?.id, user?.favorites]);

  // Clear stale deliveryStore data when anonymous user has no addresses
  // This prevents validation issues where Redux has an address ID but no actual address exists
  useEffect(() => {
    if (open && isAnonymous && addresses?.length === 0) {
      const storeAddress =
        typeof deliveryStore === 'object'
          ? deliveryStore?.address
          : deliveryStore;

      if (storeAddress) {
        dispatch(
          setDeliveryStore({
            address: '',
            store: '',
            addressData: null,
            source: null,
          })
        );
      }
    }
  }, [open, isAnonymous, addresses?.length, deliveryStore, dispatch]);

  // Create a filtered version of selectedModifiers that excludes text modifiers
  // This prevents text input changes from triggering price recalculation
  const nonTextSelectedModifiers = useMemo(() => {
    const filtered = {};
    Object.keys(selectedModifiers).forEach((modifierId) => {
      const modifier = itemModifiers?.find((m) => m.id === modifierId);
      if (modifier && !modifier.isTextInput) {
        filtered[modifierId] = selectedModifiers[modifierId];
      }
    });
    return filtered;
  }, [selectedModifiers, itemModifiers]);

  // Debounced price calculation API calls to improve performance
  useEffect(() => {
    const effectiveStoreId = storeId || user?.defaultStore;
    if (
      open &&
      item &&
      auth?.currentUser?.uid &&
      effectiveStoreId &&
      hasUserModified
    ) {
      // Increase delay to reduce API calls while user is configuring
      const timeoutId = setTimeout(() => {
        calculatePriceFromAPI();
      }, 1000); // Increased from 500ms to 1000ms

      return () => clearTimeout(timeoutId);
    }
  }, [
    open,
    item,
    hasUserModified,
    nonTextSelectedModifiers,
    quantity,
    selectedVariation,
    storeId,
    user?.defaultStore,
    calculatePriceFromAPI,
  ]);
  // Memoized display values for better performance
  const displayTotalPrice = useMemo(() => {
    // If API returned totals, subtract service charge (if present) so the displayed
    // total excludes service charge. Otherwise fall back to the local totalPrice.
    if (apiPricingData && apiPricingData.totalMoney?.amount !== undefined) {
      const totalAmt = Number(apiPricingData.totalMoney.amount) || 0;
      const serviceAmt = Number(apiPricingData.serviceChargeMoney?.amount) || 0;
      const net = Math.max(0, totalAmt - serviceAmt);
      return formatPriceFromCents(net);
    }

    return totalPrice;
  }, [apiPricingData, totalPrice]);

  // Calculate price before discount (totalMoney.amount + discountMoney.amount)
  const displayPriceBeforeDiscount = useMemo(() => {
    if (
      apiPricingData &&
      apiPricingData.totalMoney?.amount !== undefined &&
      apiPricingData.discountMoney?.amount !== undefined
    ) {
      const totalAmount = Number(apiPricingData.totalMoney.amount) || 0;
      const serviceAmt = Number(apiPricingData.serviceChargeMoney?.amount) || 0;
      const discountAmount = Number(apiPricingData.discountMoney.amount) || 0;
      const net = Math.max(0, totalAmount - serviceAmt + discountAmount);
      return formatPriceFromCents(net);
    }
    return null;
  }, [apiPricingData]);

  // Calculate price before discount and tax (totalMoney.amount + discountMoney.amount + taxMoney.amount)
  const displayPriceBeforeDiscountAndTax = useMemo(() => {
    if (
      apiPricingData &&
      apiPricingData.totalMoney?.amount !== undefined &&
      apiPricingData.discountMoney?.amount !== undefined &&
      apiPricingData.taxMoney?.amount !== undefined
    ) {
      const totalAmount = Number(apiPricingData.totalMoney.amount) || 0;
      const serviceAmt = Number(apiPricingData.serviceChargeMoney?.amount) || 0;
      const discountAmount = Number(apiPricingData.discountMoney.amount) || 0;
      const taxAmount = Number(apiPricingData.taxMoney.amount) || 0;
      const net = Math.max(
        0,
        totalAmount - serviceAmt + discountAmount + taxAmount
      );
      return formatPriceFromCents(net);
    }
    return null;
  }, [apiPricingData]);

  const displayModifierPrice = useMemo(() => {
    // For the new API structure, we don't have separate modifier price
    // We'll calculate it as the difference if needed
    return modifierPrice;
  }, [modifierPrice]);

  const displayBasePrice = useMemo(() => {
    return basePrice;
  }, [basePrice]);

  const hasUnsavedChanges = useCallback(() => {
    if (!isEditingCurrentItem || !initialEditStateRef.current) return false;
    const init = initialEditStateRef.current;
    if (quantity !== init.quantity) return true;
    if (JSON.stringify(selectedModifiers) !== init.modifiers) return true;
    if ((selectedVariation?.id || null) !== init.variationId) return true;
    return false;
  }, [isEditingCurrentItem, quantity, selectedModifiers, selectedVariation]);

  const handleCloseWithCheck = useCallback(() => {
    if (hasUnsavedChanges()) {
      setShowDiscardDialog(true);
    } else {
      handleClose();
    }
  }, [hasUnsavedChanges, handleClose]);

  if (!item) return null;
  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseWithCheck}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        {/* Discard Changes Confirmation Dialog */}
        <Dialog
          open={showDiscardDialog}
          onClose={() => setShowDiscardDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
        >
          <DialogTitle sx={{ fontFamily: 'Jost, sans-serif', fontWeight: 700 }}>
            Discard Changes?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ fontFamily: 'Jost, sans-serif', color: '#6B7280' }}>
              You have unsaved changes to this item. Are you sure you want to discard them?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setShowDiscardDialog(false)}
              disableRipple
              sx={cancelButtonSx}
            >
              Keep Editing
            </Button>
            <Button
              variant="contained"
              disableRipple
              onClick={() => {
                setShowDiscardDialog(false);
                handleClose();
              }}
              sx={confirmButtonSx}
            >
              Discard
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={storeMismatchOpen}
          onClose={() => setStoreMismatchOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: STATIC_STYLES.mismatchPaper,
          }}
        >
          <DialogTitle sx={STATIC_STYLES.mismatchTitle}>
            <Box sx={mismatchIconBoxSx}>
              <ShoppingCartIcon sx={mismatchIconSx} />
            </Box>
            <Typography variant="h6" sx={STATIC_STYLES.mismatchTitleText}>
              Clear cart to switch Branch
            </Typography>
          </DialogTitle>
          <DialogContent sx={STATIC_STYLES.mismatchContent}>
            <Typography variant="body1" sx={STATIC_STYLES.mismatchBodyText}>
              Your cart already has items from a different branch. To add items
              from this branch, you need to clear your current cart.
            </Typography>
            <Box sx={STATIC_STYLES.mismatchWarningBox}>
              <Typography
                variant="body2"
                sx={STATIC_STYLES.mismatchWarningText}
              >
                This will remove all items currently in your cart.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={STATIC_STYLES.mismatchActions}>
            <Button
              onClick={() => setStoreMismatchOpen(false)}
              variant="outlined"
              disableRipple
              sx={cancelButtonSx}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disableRipple
              onClick={handleClearCartAndProceed}
              sx={confirmButtonSx}
            >
              Clear cart
            </Button>
          </DialogActions>
        </Dialog>
        {/* Close Button */}
        <IconButton onClick={handleCloseWithCheck} sx={STATIC_STYLES.closeButton}>
          <CloseIcon />
        </IconButton>

        <DialogContent sx={STATIC_STYLES.dialogContent}>
          <Grid container sx={STATIC_STYLES.gridContainer}>
            {/* Gallery Section */}
            <Grid item xs={12} md={6} sx={gridItemSx}>
              <Box sx={galleryBoxSx}>
                {/* Main Image */}
                <Box
                  role="img"
                  aria-label={`${item.name} - product image ${
                    selectedImageIndex + 1
                  } of ${images.length}`}
                  sx={{
                    flex: 1,
                    borderRadius: '20px',
                    overflow: 'hidden',
                    backgroundImage: `url(${
                      images[selectedImageIndex] || item.item_image_url || logo
                    })`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundColor: '#F5F5F5',
                    minHeight: isMobile ? '280px' : 'auto',
                    height: isMobile ? '280px' : 'auto',
                  }}
                />

                {images.length > 1 && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMobile ? 'row' : 'column',
                      alignItems: 'center',
                      gap: 1,
                      height: isMobile ? 'auto' : '100%',
                      width: isMobile ? '100%' : '120px',
                      justifyContent: 'center',
                    }}
                  >
                    {/* Left/Up Arrow */}
                    <IconButton
                      onClick={() =>
                        handleImageNavigation(isMobile ? 'left' : 'up')
                      }
                      disabled={selectedImageIndex === 0}
                      sx={{
                        color:
                          selectedImageIndex === 0
                            ? '#ccc'
                            : environment.mainColor,
                        width: isMobile ? '32px' : '40px',
                        height: isMobile ? '32px' : '40px',
                      }}
                    >
                      {isMobile ? (
                        <KeyboardArrowLeftIcon />
                      ) : (
                        <KeyboardArrowUpIcon />
                      )}
                    </IconButton>

                    {/* Thumbnails */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'row' : 'column',
                        gap: isMobile ? 0.5 : 1,
                        overflowX: isMobile ? 'auto' : 'hidden',
                        overflowY: isMobile ? 'hidden' : 'auto',
                        padding: isMobile ? 0.5 : 1,
                        flex: 1,
                        maxHeight: isMobile ? 'none' : '350px',
                        width: isMobile ? 'auto' : '100%',
                        justifyContent: 'center',
                      }}
                    >
                      {images.map((image, index) => (
                        <Box
                          key={index}
                          role="img"
                          aria-label={`${item.name} - thumbnail ${
                            index + 1
                          } of ${images.length}${
                            selectedImageIndex === index ? ' (selected)' : ''
                          }`}
                          onClick={() => handleSmallImageClick(index)}
                          sx={{
                            ...getThumbnailSx(selectedImageIndex === index),
                            backgroundImage: `url(${image})`,
                          }}
                        />
                      ))}
                    </Box>

                    {/* Right/Down Arrow */}
                    <IconButton
                      onClick={() =>
                        handleImageNavigation(isMobile ? 'right' : 'down')
                      }
                      disabled={selectedImageIndex === images.length - 1}
                      sx={{
                        color:
                          selectedImageIndex === images.length - 1
                            ? '#ccc'
                            : environment.mainColor,
                        width: isMobile ? '32px' : '40px',
                        height: isMobile ? '32px' : '40px',
                      }}
                    >
                      {isMobile ? (
                        <KeyboardArrowRightIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>

            {/* Content Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                height: isMobile ? '100%' : '90vh',
                // overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  padding: isMobile ? 2 : 3,
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  overflowY: 'auto',
                  paddingBottom: 0,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a8a8a8',
                  },
                }}
              >
                {/* Section 1: Title and Price */}
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="h4"
                      component="h1"
                      sx={{
                        fontFamily: '"Jost", sans-serif',
                        fontWeight: 700,
                        color: '#172B4D',
                        fontSize: isMobile ? '24px' : '28px',
                        textTransform: 'capitalize',
                        flex: 1,
                      }}
                    >
                      {item.name}
                    </Typography>
                    <img
                      src={halal}
                      alt="Halal logo"
                      loading="lazy"
                      decoding="async"
                      style={{
                        width: '56px',
                        height: '56px',
                        marginRight: '36px',
                      }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Price Display: prefer API response when available */}
                    {apiPricingData &&
                    apiPricingData.totalMoney?.amount !== undefined ? (
                      // If API returned discount > 0, show discounted + original (struck-through)
                      parseFloat(apiPricingData.discountMoney?.amount || '0') >
                      0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            component="span"
                            sx={{
                              fontSize: isMobile ? '20px' : '24px',
                              fontWeight: 700,
                              color: '#2C742F',
                              fontFamily: '"Jost", sans-serif',
                            }}
                          >
                            {isCalculatingPrice
                              ? 'Calculating...'
                              : `$${displayTotalPrice}`}
                          </Typography>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: isMobile ? '16px' : '18px',
                              fontWeight: 400,
                              color: '#999999',
                              fontFamily: '"Jost", sans-serif',
                              textDecoration: 'line-through',
                            }}
                          >
                            ${displayPriceBeforeDiscount}
                          </Typography>
                        </Box>
                      ) : (
                        // API returned no discount: show the API total
                        <Typography
                          component="span"
                          sx={{
                            fontSize: isMobile ? '20px' : '24px',
                            fontWeight: 700,
                            color: '#2C742F',
                            fontFamily: '"Jost", sans-serif',
                          }}
                        >
                          {isCalculatingPrice
                            ? 'Calculating...'
                            : `$${displayTotalPrice}`}
                        </Typography>
                      )
                    ) : // Fallback to Firebase/item-level discount if API not available
                    item?.defaultPriceAfterDiscount &&
                      item?.defaultPriceAfterDiscount !== item?.price &&
                      item?.discounts &&
                      item.discounts.some((discount) =>
                        isDiscountValidForCurrentStore(discount)
                      ) ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: isMobile ? '20px' : '24px',
                            fontWeight: 700,
                            color: '#2C742F',
                            fontFamily: '"Jost", sans-serif',
                          }}
                        >
                          {isCalculatingPrice
                            ? 'Calculating...'
                            : `$${formatPriceFromCents(
                                item?.defaultPriceAfterDiscount
                              )}`}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: isMobile ? '16px' : '18px',
                            fontWeight: 400,
                            color: '#999999',
                            fontFamily: '"Jost", sans-serif',
                            textDecoration: 'line-through',
                          }}
                        >
                          ${formatPriceFromCents(item?.price)}
                        </Typography>
                      </Box>
                    ) : (
                      // Final fallback: show computed frontend price
                      <Typography
                        component="span"
                        sx={{
                          fontSize: isMobile ? '20px' : '24px',
                          fontWeight: 700,
                          color: '#2C742F',
                          fontFamily: '"Jost", sans-serif',
                        }}
                      >
                        {isCalculatingPrice
                          ? 'Calculating...'
                          : `$${displayTotalPrice}`}
                      </Typography>
                    )}

                    {/* Show discount badges */}
                    {((hasUserModified &&
                      apiPricingData &&
                      parseFloat(apiPricingData.discountMoney?.amount || '0') >
                        0) ||
                      (!hasUserModified &&
                        item.discounts &&
                        item.discounts.filter((discount) =>
                          isDiscountValidForCurrentStore(discount)
                        ).length > 0)) && (
                      <Box
                        sx={{
                          backgroundColor: '#EA4B481A',
                          color: '#EA4B48',
                          padding: '3px 10px',
                          borderRadius: '30px',
                          fontSize: '14px',
                          fontWeight: 500,
                          fontFamily: '"Jost", sans-serif',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {hasUserModified &&
                        apiPricingData &&
                        parseFloat(
                          apiPricingData.discountMoney?.amount || '0'
                        ) > 0
                          ? `DISCOUNT APPLIED (-$${formatPriceFromCents(
                              apiPricingData.discountMoney.amount
                            )})`
                          : 'DISCOUNT AVAILABLE'}
                        {!hasUserModified &&
                          item.discounts &&
                          item.discounts.filter((discount) =>
                            isDiscountValidForCurrentStore(discount)
                          ).length > 1 && (
                            <Box
                              component="span"
                              sx={{
                                backgroundColor: '#EA4B48',
                                color: 'white',
                                borderRadius: '50%',
                                width: '16px',
                                height: '16px',
                                fontSize: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                              }}
                            >
                              {
                                item.discounts.filter((discount) =>
                                  isDiscountValidForCurrentStore(discount)
                                ).length
                              }
                            </Box>
                          )}
                      </Box>
                    )}
                  </Box>

                  {/* Price Breakdown */}
                  <Box sx={{ marginTop: 1 }}>
                    {/* Show discount information from item data for UI only */}
                    {!hasUserModified &&
                      item.discounts &&
                      item.discounts.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {/* {item.discounts
                            .filter((discount) =>
                              isDiscountValidForCurrentStore(discount)
                            )
                            .map((discount, index) => (
                              <Typography
                                key={discount.id || index}
                                component="div"
                                sx={{
                                  fontSize: '12px',
                                  color: '#EA4B48',
                                  fontFamily: '"Jost", sans-serif',
                                  ml: 1,
                                }}
                              >
                                • {discount.name}
                                {discount.discountType === 'FIXED_PERCENTAGE' &&
                                  ` (${discount.percentage}% OFF)`}
                                {discount.discountType === 'FIXED_AMOUNT' &&
                                  ` (-$${formatPriceFromCents(
                                    discount.amountMoney?.amount || 0
                                  )})`}
                              </Typography>
                            ))} */}
                          {/* Only show "Discounts applied at checkout" if there are valid discounts */}
                          {/* {item.discounts.filter((discount) =>
                            isDiscountValidForCurrentStore(discount)
                          ).length > 0 && (
                            <Typography
                              component="div"
                              sx={{
                                fontSize: '13px',
                                color: '#EA4B48',
                                fontFamily: '"Jost", sans-serif',
                                fontWeight: 600,
                                borderTop: '1px solid #EA4B4820',
                                pt: 0.5,
                                mt: 0.5,
                              }}
                            >
                              Discounts applied at checkout
                            </Typography>
                          )} */}
                        </Box>
                      )}

                    {/* Show API-calculated breakdown when API data is available (show tax/discount even on initial open) */}
                    {apiPricingData && (
                      <Box sx={{ mt: 0.5 }}>
                        {parseFloat(
                          apiPricingData.discountMoney?.amount || '0'
                        ) > 0 && (
                          <Typography
                            component="div"
                            sx={{
                              fontSize: '14px',
                              color: '#EA4B48',
                              fontFamily: '"Jost", sans-serif',
                            }}
                          >
                            Discount: -$
                            {formatPriceFromCents(
                              apiPricingData.discountMoney.amount
                            )}
                          </Typography>
                        )}
                        {parseFloat(apiPricingData.taxMoney?.amount || '0') >
                          0 && (
                          <Typography
                            component="div"
                            sx={{
                              fontSize: '14px',
                              color: '#666',
                              fontFamily: '"Jost", sans-serif',
                            }}
                          >
                            Tax: +$
                            {formatPriceFromCents(
                              apiPricingData.taxMoney.amount
                            )}
                          </Typography>
                        )}
                        {/* {parseFloat(
                        apiPricingData.serviceChargeMoney?.amount || '0'
                      ) > 0 && (
                        <Typography
                          component="div"
                          sx={{
                            fontSize: '14px',
                            color: '#666',
                            fontFamily: '"Jost", sans-serif',
                          }}
                        >
                          Service Charge: +$
                          {formatPriceFromCents(
                            apiPricingData.serviceChargeMoney.amount
                          )}
                        </Typography>
                      )} */}
                        {quantity > 1 && (
                          <Typography
                            component="div"
                            sx={{
                              fontSize: '14px',
                              color: '#666',
                              fontFamily: '"Jost", sans-serif',
                            }}
                          >
                            Quantity: {quantity} × $
                            {(
                              (parseFloat(apiPricingData.totalMoney.amount) -
                                (parseFloat(
                                  apiPricingData.serviceChargeMoney?.amount
                                ) || 0)) /
                              (quantity * 100)
                            ).toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Show Firebase-based breakdown when user hasn't modified */}
                    {!hasUserModified && (
                      <>
                        {displayModifierPrice > 0 && (
                          <Typography
                            component="div"
                            sx={{
                              fontSize: '14px',
                              color: '#666',
                              fontFamily: '"Jost", sans-serif',
                            }}
                          >
                            Customization: +${displayModifierPrice.toFixed(2)}{' '}
                            each
                          </Typography>
                        )}
                        {quantity > 1 && (
                          <Typography
                            component="div"
                            sx={{
                              fontSize: '14px',
                              color: '#666',
                              fontFamily: '"Jost", sans-serif',
                            }}
                          >
                            Quantity: {quantity} × $
                            {(displayBasePrice + displayModifierPrice).toFixed(
                              2
                            )}
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                <Divider sx={{ marginY: 2 }} />

                {/* Section 2: Size/Variations Selection */}
                {item?.variations && item.variations.length > 0 && (
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Jost", sans-serif',
                        fontWeight: 600,
                        color: '#172B4D',
                        marginBottom: 2,
                        fontSize: '18px',
                      }}
                    >
                      Size/Variation
                      <span style={{ color: environment.mainColor }}>
                        {' '}
                        (Required)
                      </span>
                    </Typography>

                    {validationErrors['variation'] && (
                      <Typography
                        sx={{
                          fontFamily: '"Jost", sans-serif',
                          fontWeight: 500,
                          color: '#f44336',
                          fontSize: '14px',
                          marginBottom: 1,
                        }}
                      >
                        {validationErrors['variation']}
                      </Typography>
                    )}

                    <FormControl component="fieldset" sx={{ width: '100%' }}>
                      <RadioGroup
                        value={selectedVariation ? selectedVariation.id : ''}
                        onChange={(e) => {
                          const variation = item.variations.find(
                            (v) => v.id === e.target.value
                          );
                          setSelectedVariation(variation);
                          setHasUserModified(true);
                          // Clear validation error when variation is selected
                          if (validationErrors['variation']) {
                            setValidationErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors['variation'];
                              return newErrors;
                            });
                          }
                        }}
                      >
                        {item.variations.map((variation) => (
                          <FormControlLabel
                            key={variation.id}
                            value={variation.id}
                            control={<Radio sx={radioSx} />}
                            label={
                              <Box sx={STATIC_STYLES.variationBox}>
                                <Typography sx={STATIC_STYLES.variationTitle}>
                                  {variation.name}
                                </Typography>
                                <Typography sx={STATIC_STYLES.variationPrice}>
                                  ${formatPriceFromCents(variation.price)}
                                </Typography>
                              </Box>
                            }
                            sx={{
                              margin: 0,
                              padding: '12px 16px',
                              width: '100%',
                              border: '1px solid #E0E0E0',
                              borderRadius: '12px',
                              marginBottom: 1,
                              backgroundColor:
                                selectedVariation?.id === variation.id
                                  ? 'rgba(253, 86, 34, 0.05)'
                                  : '#FAFAFA',
                              '&:hover': {
                                backgroundColor: 'rgba(253, 86, 34, 0.1)',
                                borderColor: environment.mainColor,
                              },
                              ...(selectedVariation?.id === variation.id && {
                                borderColor: environment.mainColor,
                                backgroundColor: 'rgba(253, 86, 34, 0.1)',
                              }),
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Box>
                )}

                <Divider sx={{ marginY: 2 }} />

                {/* Section 3: Description */}
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: '"Jost", sans-serif',
                      fontWeight: 600,
                      color: '#172B4D',
                      marginBottom: 1,
                      fontSize: '18px',
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontFamily: '"Jost", sans-serif',
                      fontWeight: 400,
                      color: '#666',
                      fontSize: '15px',
                      lineHeight: 1.6,
                      textTransform: 'capitalize',
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>

                <Divider sx={{ marginY: 2 }} />

                {/* Section 4: Modifiers */}
                {itemModifiers && itemModifiers.length > 0 && (
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: '"Jost", sans-serif',
                        fontWeight: 600,
                        color: '#172B4D',
                        marginBottom: 2,
                        fontSize: '18px',
                      }}
                    >
                      Customize Your Order
                    </Typography>

                    {itemModifiers.map((modifier, index) => (
                      <Box
                        key={modifier.id}
                        ref={(el) => (modifierRefs.current[modifier.id] = el)}
                        sx={{
                          border: validationErrors[modifier.id]
                            ? '2px solid #f44336'
                            : '1px solid #E0E0E0',
                          borderRadius: '12px',
                          marginBottom: 2,
                          overflow: 'hidden',
                          backgroundColor: '#FAFAFA',
                        }}
                      >
                        {/* Modifier Header */}
                        <Box
                          onClick={() => toggleModifierExpanded(modifier.id)}
                          sx={{
                            padding: 2,
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: '#FFF',
                            borderBottom: expandedModifiers[modifier.id]
                              ? '1px solid #E0E0E0'
                              : 'none',
                            '&:hover': {
                              backgroundColor: '#F5F5F5',
                            },
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontFamily: '"Jost", sans-serif',
                                fontWeight: 600,
                                color: '#172B4D',
                                fontSize: '16px',
                                textTransform: 'capitalize',
                              }}
                            >
                              {modifier.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: '"Jost", sans-serif',
                                fontWeight: 400,
                                color: '#666',
                                fontSize: '13px',
                                marginTop: 0.5,
                              }}
                            >
                              {modifier.isTextInput
                                ? 'Enter your custom text'
                                : modifier.minPermitted ===
                                  modifier.maxPermitted
                                ? `Choose ${modifier.minPermitted}`
                                : `Choose ${modifier.minPermitted}-${modifier.maxPermitted}`}
                              {(modifier.minPermitted > 0 ||
                                modifier.isRequired) && (
                                <span style={{ color: environment.mainColor }}>
                                  {' '}
                                  (Required)
                                </span>
                              )}
                            </Typography>
                            {validationErrors[modifier.id] && (
                              <Typography
                                sx={{
                                  fontFamily: '"Jost", sans-serif',
                                  fontWeight: 500,
                                  color: '#f44336',
                                  fontSize: '12px',
                                  marginTop: 0.5,
                                }}
                              >
                                {validationErrors[modifier.id]}
                              </Typography>
                            )}
                          </Box>
                          {expandedModifiers[modifier.id] ? (
                            <ExpandLessIcon
                              sx={{ color: environment.mainColor }}
                            />
                          ) : (
                            <ExpandMoreIcon
                              sx={{ color: environment.mainColor }}
                            />
                          )}
                        </Box>

                        {/* Modifier Options */}
                        <Collapse in={expandedModifiers[modifier.id]}>
                          <Box sx={{ padding: 2, paddingTop: 1 }}>
                            {modifier.isTextInput ? (
                              // Text input for special requests/notes
                              <TextField
                                fullWidth
                                multiline
                                rows={3}
                                placeholder={`Enter your ${modifier.title.toLowerCase()}...`}
                                value={
                                  selectedModifiers[modifier.id]?.[0]
                                    ? typeof selectedModifiers[
                                        modifier.id
                                      ][0] === 'object'
                                      ? selectedModifiers[modifier.id][0]
                                          .text || ''
                                      : selectedModifiers[modifier.id][0]
                                    : ''
                                }
                                onChange={(e) =>
                                  handleModifierChange(
                                    modifier.id,
                                    null,
                                    null,
                                    e.target.value
                                  )
                                }
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    fontFamily: '"Jost", sans-serif',
                                    '&:hover fieldset': {
                                      borderColor: environment.mainColor,
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: environment.mainColor,
                                    },
                                  },
                                }}
                              />
                            ) : modifier.isSingleSelection ? (
                              // Radio buttons for single selection (selection_type === 'SINGLE')
                              <FormControl
                                component="fieldset"
                                sx={{ width: '100%' }}
                              >
                                <RadioGroup
                                  value={
                                    selectedModifiers[modifier.id]?.[0]
                                      ? typeof selectedModifiers[
                                          modifier.id
                                        ][0] === 'object'
                                        ? selectedModifiers[modifier.id][0].id
                                        : selectedModifiers[modifier.id][0]
                                      : ''
                                  }
                                  onChange={(e) =>
                                    handleModifierChange(
                                      modifier.id,
                                      e.target.value
                                    )
                                  }
                                >
                                  {modifier.options?.map((option) => (
                                    <FormControlLabel
                                      key={option.id}
                                      value={option.id}
                                      control={<Radio sx={radioSx} />}
                                      label={
                                        <ModifierRadioOption
                                          option={option}
                                          mainColor={environment.mainColor}
                                          formatPriceFromCents={
                                            formatPriceFromCents
                                          }
                                        />
                                      }
                                      sx={STATIC_STYLES.formControlLabel}
                                    />
                                  )) || []}
                                </RadioGroup>
                              </FormControl>
                            ) : (
                              // Checkboxes for multiple selection
                              <FormGroup>
                                {modifier.options?.map((option) => (
                                  <Box key={option.id}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          checked={
                                            selectedModifiers[
                                              modifier.id
                                            ]?.some((item) =>
                                              typeof item === 'object'
                                                ? item.id === option.id
                                                : item === option.id
                                            ) || false
                                          }
                                          onChange={(e) =>
                                            handleModifierChange(
                                              modifier.id,
                                              option.id,
                                              e.target.checked
                                            )
                                          }
                                          sx={checkboxSx}
                                        />
                                      }
                                      label={
                                        <ModifierCheckboxLabel
                                          option={option}
                                          mainColor={environment.mainColor}
                                          formatPriceFromCents={
                                            formatPriceFromCents
                                          }
                                        />
                                      }
                                      sx={STATIC_STYLES.formControlLabel}
                                    />
                                    {/* Quantity controls for selected modifiers */}
                                    {(modifier.allow_quantities ||
                                      modifier.allowQuantities) &&
                                      selectedModifiers[modifier.id]?.some(
                                        (item) =>
                                          typeof item === 'object'
                                            ? item.id === option.id
                                            : item === option.id
                                      ) && (
                                        <Box sx={STATIC_STYLES.quantityBox}>
                                          <Typography
                                            sx={STATIC_STYLES.quantityLabel}
                                          >
                                            Quantity:
                                          </Typography>
                                          <Box
                                            sx={STATIC_STYLES.quantityContainer}
                                          >
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleModifierQuantityChange(
                                                  modifier.id,
                                                  option.id,
                                                  'decrease'
                                                )
                                              }
                                              sx={STATIC_STYLES.quantityButton}
                                            >
                                              <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <Typography
                                              sx={STATIC_STYLES.quantityText}
                                            >
                                              {(() => {
                                                const selectedItem =
                                                  selectedModifiers[
                                                    modifier.id
                                                  ]?.find((item) =>
                                                    typeof item === 'object'
                                                      ? item.id === option.id
                                                      : item === option.id
                                                  );
                                                return selectedItem &&
                                                  typeof selectedItem ===
                                                    'object'
                                                  ? selectedItem.quantity || 1
                                                  : 1;
                                              })()}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleModifierQuantityChange(
                                                  modifier.id,
                                                  option.id,
                                                  'increase'
                                                )
                                              }
                                              sx={{
                                                width: 28,
                                                height: 28,
                                                color: environment.mainColor,
                                              }}
                                            >
                                              <AddIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Box>
                                      )}
                                  </Box>
                                )) || []}
                              </FormGroup>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {/* Fixed Section 5: Quantity, Add to Cart, and Favorite */}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  padding: isMobile ? 2 : 3,
                  paddingTop: isMobile ? 1 : 2,
                  borderTop: '1px solid #E0E0E0',
                  backgroundColor: 'white',
                  zIndex: 10,
                  boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  {/* Quantity control stays on the left */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: '2px solid #F0F0F0',
                      borderRadius: '25px',
                      padding: '8px',
                      width: isMobile ? 'auto' : 'fit-content',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                    }}
                  >
                    <IconButton
                      onClick={() => handleQuantityChange('decrease')}
                      disabled={quantity <= 1}
                      sx={{
                        color: quantity <= 1 ? '#ccc' : environment.mainColor,
                        '&:hover': {
                          backgroundColor: 'rgba(253, 86, 34, 0.1)',
                        },
                        padding: isMobile ? '6px' : undefined,
                      }}
                    >
                      <RemoveIcon />
                    </IconButton>

                    <Typography
                      sx={{
                        minWidth: '40px',
                        textAlign: 'center',
                        fontFamily: '"Jost", sans-serif',
                        fontWeight: 600,
                        fontSize: isMobile ? '16px' : '18px',
                        color: '#172B4D',
                      }}
                    >
                      {quantity}
                    </Typography>

                    <IconButton
                      onClick={() => handleQuantityChange('increase')}
                      sx={{
                        color: environment.mainColor,
                        '&:hover': {
                          backgroundColor: 'rgba(253, 86, 34, 0.1)',
                        },
                        padding: isMobile ? '6px' : undefined,
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>

                  {/* On mobile, group Add to Cart and Favorite side-by-side; on desktop keep existing layout */}
                  {isMobile ? (
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleAddToCart}
                        disabled={loading || isCalculatingPrice}
                        sx={{
                          backgroundColor: added
                            ? '#4CAF50'
                            : environment.mainColor,
                          color: '#FFFFFF',
                          borderRadius: '12px',
                          padding: '12px 18px',
                          fontFamily: '"Jost", sans-serif',
                          fontWeight: 600,
                          fontSize: '14px',
                          textTransform: 'none',
                          boxShadow: 'none',
                          border: 'none',
                          flex: 1,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: added
                              ? '#45a049'
                              : `${environment.mainColor}CC`,
                            transform: 'translateY(-2px)',
                            boxShadow: 'none',
                          },
                          '&:disabled': {
                            backgroundColor: '#ccc',
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        }}
                        endIcon={<ShoppingCartIcon sx={{ fontSize: '18px' }} />}
                      >
                        {loading
                          ? 'Adding...'
                          : isCalculatingPrice
                          ? 'Calculating...'
                          : added
                          ? '✓ Added'
                          : isEditingCurrentItem
                          ? 'Update Item'
                          : 'Add to Cart'}
                      </Button>

                      <IconButton
                        onClick={handleFavoriteToggle}
                        sx={{
                          backgroundColor: isFavorite
                            ? `${environment.mainColor}1A`
                            : `${environment.mainColor}14`,
                          color: isFavorite ? environment.mainColor : '#A01913',
                          borderRadius: '12px',
                          width: '44px',
                          height: '44px',
                          '&:hover': {
                            backgroundColor: `${environment.mainColor}1A`,
                            borderColor: environment.mainColor,
                            color: environment.mainColor,
                          },
                        }}
                      >
                        {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                    </Box>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        onClick={handleAddToCart}
                        disabled={loading || isCalculatingPrice}
                        sx={{
                          backgroundColor: added
                            ? '#4CAF50'
                            : environment.mainColor,
                          color: '#FFFFFF',
                          borderRadius: '43px',
                          padding: '16px 40px',
                          fontFamily: '"Jost", sans-serif',
                          fontWeight: 600,
                          fontSize: '16px',
                          textTransform: 'none',
                          boxShadow: 'none',
                          border: 'none',
                          flex: 1,
                          width: 'auto',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: added
                              ? '#45a049'
                              : `${environment.mainColor}CC`,
                            transform: 'translateY(-2px)',
                            boxShadow: 'none',
                          },
                          '&:disabled': {
                            backgroundColor: '#ccc',
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        }}
                        endIcon={<ShoppingCartIcon sx={{ fontSize: '20px' }} />}
                      >
                        {loading
                          ? 'Adding...'
                          : isCalculatingPrice
                          ? 'Calculating...'
                          : added
                          ? '✓ Added to Cart'
                          : isEditingCurrentItem
                          ? 'Update Item'
                          : 'Add to Cart'}
                      </Button>

                      <IconButton
                        onClick={handleFavoriteToggle}
                        sx={{
                          backgroundColor: isFavorite
                            ? `${environment.mainColor}1A`
                            : `${environment.mainColor}14`,
                          color: isFavorite ? environment.mainColor : '#A01913',
                          borderRadius: '43px',
                          width: '50px',
                          height: '50px',
                          '&:hover': {
                            backgroundColor: `${environment.mainColor}1A`,
                            borderColor: environment.mainColor,
                            color: environment.mainColor,
                          },
                        }}
                      >
                        {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                    </>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Address Dialog for delivery address selection */}
      <DeliveryDialog
        open={addressDialogOpen}
        handleDefault={setDefaultAdd}
        handleCloseCartDialoge={() => {}} // Not needed in this context
        token={user?.uid}
        setErr={setAddressErr}
        handleClose={() => setAddressDialogOpen(false)}
        items={addresses}
        setAddressStore={setAddressStore}
        handleFee={setDeliveryFee}
        setApiError={setApiError}
        onAddressChange={handleChangeAddress}
      />
    </>
  );
};

export default memo(FattyPattyDialog);
